// src/Layouts/GateEntry/parts/TodayGateLog.tsx
//
// School-wide gate log for one day — everyone recorded, newest first, no
// class needed. GET /api/movements returns class/section/roll per row plus
// per-type counts for the chips.
//
// Narrowing: All / Entries / Exits, free-text search (name / SP ID /
// admission no) and an optional class + section. Late arrivals and exits
// without a pass are flagged on the rows themselves; the parent's "Late today"
// and "Left without pass" tiles open this log pre-filtered via `typeFilter`.

import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../../../components/CustomPaginator';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import ProfilePictureHover from '../../../components/ProfilePictureHover';
import { formatPassDate, formatTime, toDateParam } from '../../GatePasses/parts/types';

interface ClassChip {
  id: string;
  label: string;
  sections?: string[];
}

interface LogRow {
  _id: string;
  student_id: string;
  student_name?: string;
  sp_student_id?: string;
  class_name?: string;
  section?: string;
  roll_no?: number;
  direction: 'IN' | 'OUT';
  type: string;
  gate: string;
  method: string;
  timestamp: string;
  recorded_by_name?: string;
  photo_url?: string;
}

export type LogTypeFilter = 'late' | 'unauthorized' | null;

interface Props {
  classOptions: ClassChip[];
  /** Bumped by the parent after it records something, to refetch. */
  refreshKey: number;
  /** Poll every 30 s on its own — off while the gate socket pushes updates. */
  autoRefresh?: boolean;
  /** Set when the parent's Late / Left-without-pass tile was clicked. */
  typeFilter: LogTypeFilter;
  onTypeFilterChange: (f: LogTypeFilter) => void;
}

type DirectionFilter = 'all' | 'IN' | 'OUT';

const TYPE_FILTER_LABEL: Record<Exclude<LogTypeFilter, null>, { label: string; color: string; bg: string }> = {
  late: { label: 'Late arrivals only', color: '#b45309', bg: 'rgba(245,158,11,.12)' },
  unauthorized: { label: 'Left without pass only', color: '#b91c1c', bg: 'rgba(239,68,68,.10)' },
};

const TYPE_LABEL: Record<string, string> = {
  regular: 'On time',
  late: 'Late',
  dismissal: 'Dismissal',
  short_leave: 'Short leave out',
  return: 'Back from short leave',
  early_departure: 'Early departure',
  unauthorized: 'Left without pass',
};

const TodayGateLog: React.FC<Props> = ({ classOptions, refreshKey, autoRefresh = true, typeFilter, onTypeFilterChange }) => {
  const toast = useRef<Toast>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [date, setDate] = useState<Date>(() => new Date());
  const [direction, setDirection] = useState<DirectionFilter>('all');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [classId, setClassId] = useState<string | null>(null);
  const [section, setSection] = useState<string | null>(null);

  const [rows, setRows] = useState<LogRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 25, total_items: 0, total_pages: 1 });

  const isToday = toDateParam(date) === toDateParam(new Date());
  const selectedClass = classOptions.find((c) => c.id === classId) || null;

  const fetchLog = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        // gate_only: normal IN / final OUT only — short-leave trips belong to Gate Passes.
        const params: any = { date: toDateParam(date), page, limit: pageSize, gate_only: '1' };
        if (direction !== 'all') params.direction = direction;
        if (typeFilter) params.type = typeFilter;
        if (search.trim().length >= 2) params.q = search.trim();
        if (classId) params.class_id = classId;
        if (classId && section) params.section = section;

        const res = await apiClient.get('api/movements', { params });
        if (res.data.status === 'success') {
          setRows(res.data.data || []);
          setCounts(res.data.counts || {});
          setPagination(res.data.pagination);
        } else if (!quiet) {
          toast.current?.show({
            severity: 'error',
            summary: 'Not loaded',
            detail: res.data.message || 'The gate log could not be loaded.',
            life: 4000,
          });
        }
      } catch (err: any) {
        if (!quiet) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || 'Could not reach the gate log.',
            life: 5000,
          });
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [date, direction, typeFilter, search, classId, section, page, pageSize],
  );

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  // A tile click from the parent starts from page 1, across both directions.
  useEffect(() => {
    setPage(1);
    if (typeFilter) setDirection('all');
  }, [typeFilter]);

  // Parent recorded something → refresh quietly.
  useEffect(() => {
    if (refreshKey > 0) fetchLog(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Today's log keeps itself current while it's open.
  useEffect(() => {
    if (!isToday || !autoRefresh) return;
    const t = setInterval(() => fetchLog(true), 30_000);
    return () => clearInterval(t);
  }, [isToday, autoRefresh, fetchLog]);

  const onSearchChange = (v: string) => {
    setSearchText(v);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(v);
      setPage(1);
    }, 400);
  };

  // ── templates ──────────────────────────────────────────────────────────────

  const serialTemplate = (_: LogRow, o: ColumnBodyOptions): JSX.Element => (
    <div style={serialStyle}>{o.rowIndex + (page - 1) * pageSize + 1}</div>
  );

  const timeTemplate = (r: LogRow) => (
    <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>{formatTime(r.timestamp)}</span>
  );

  const studentTemplate = (r: LogRow) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <ProfilePictureHover src={r.photo_url} name={r.student_name || '?'} size={34} borderRadius={9} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{r.student_name || '—'}</div>
        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{r.sp_student_id}</div>
      </div>
    </div>
  );

  const classTemplate = (r: LogRow) => (
    <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
      {r.class_name ? `${r.class_name}${r.section ? `-${r.section}` : ''}` : '—'}
      {typeof r.roll_no === 'number' && (
        <span style={{ display: 'block', fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>Roll {r.roll_no}</span>
      )}
    </div>
  );

  const directionTemplate = (r: LogRow) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 10px',
        borderRadius: 8,
        fontSize: 11.5,
        fontWeight: 800,
        color: 'white',
        background: r.direction === 'IN' ? '#16a34a' : '#334155',
      }}
    >
      <i className={r.direction === 'IN' ? 'pi pi-sign-in' : 'pi pi-sign-out'} style={{ fontSize: 10 }} />
      {r.direction}
    </span>
  );

  const typeTemplate = (r: LogRow) => {
    const warn = r.type === 'unauthorized' || r.type === 'late';
    return (
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: r.type === 'unauthorized' ? '#b91c1c' : r.type === 'late' ? '#b45309' : '#475569',
        }}
      >
        {warn && <i className="pi pi-exclamation-circle" style={{ fontSize: 11, marginRight: 5 }} />}
        {TYPE_LABEL[r.type] || r.type}
      </span>
    );
  };

  const gateTemplate = (r: LogRow) => (
    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, textTransform: 'capitalize' }}>
      {r.gate} gate
      <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>
        {r.method}
        {r.recorded_by_name ? ` · ${r.recorded_by_name}` : ''}
      </span>
    </div>
  );

  const total = counts.IN || counts.OUT ? (counts.IN || 0) + (counts.OUT || 0) : 0;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        .gl-toolbar { background:white; border-radius:16px; padding:14px 18px; margin-bottom:16px; box-shadow:0 2px 8px rgba(30,64,175,.08); border:1px solid rgba(59,130,246,.12); }
        .gl-seg { display:inline-flex; gap:4px; background:#f1f5f9; border-radius:12px; padding:4px; }
        .gl-seg-btn { border:none; background:transparent; padding:7px 16px; border-radius:9px; font-size:13px; font-weight:600; color:#64748b; cursor:pointer; display:inline-flex; align-items:center; gap:6px; }
        .gl-seg-btn.active { background:white; color:#1e40af; box-shadow:0 1px 4px rgba(15,23,42,.1); }
        .gl-class { padding:5px 12px; border-radius:8px; border:1.5px solid #e5e7eb; background:white; color:#1f2937; font-size:11.5px; font-weight:600; cursor:pointer; }
        .gl-class.active { border-color:#3b82f6; background:linear-gradient(135deg,#1e40af 0%,#3b82f6 50%,#60a5fa 100%); color:white; }
      `}</style>

      <div className="gl-toolbar">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: 190, maxWidth: 220, flex: '0 1 200px' }}>
            <GlobalDateField
              label="Date"
              value={date}
              onChange={(val) => {
                setDate((val as Date | null) ?? new Date());
                setPage(1);
              }}
              dateFormat="dd M yy"
              icon="pi pi-calendar"
              maxDate={new Date()}
              fullWidth
            />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <GlobalInputField
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Find a student — name, SP ID or admission no"
              icon="pi pi-search"
              autoComplete="off"
            />
          </div>
          <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 600, color: '#64748b' }}>
            <span style={{ color: '#1e40af', fontWeight: 800 }}>{total}</span> gate records{' '}
            {isToday ? 'today' : `on ${formatPassDate(toDateParam(date))}`}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
          <div className="gl-seg">
            {(
              [
                { v: 'all', label: 'All', icon: '' },
                { v: 'IN', label: 'Entries', icon: 'pi pi-sign-in' },
                { v: 'OUT', label: 'Exits', icon: 'pi pi-sign-out' },
              ] as { v: DirectionFilter; label: string; icon: string }[]
            ).map((d) => (
              <button
                key={d.v}
                className={`gl-seg-btn ${direction === d.v ? 'active' : ''}`}
                onClick={() => {
                  setDirection(d.v);
                  if (typeFilter) onTypeFilterChange(null); // a direction pick replaces a tile filter
                  setPage(1);
                }}
              >
                {d.icon && <i className={d.icon} style={{ fontSize: 11 }} />}
                {d.label}
              </button>
            ))}
          </div>

          {typeFilter && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px 6px 12px',
                borderRadius: 20,
                fontSize: 12.5,
                fontWeight: 700,
                color: TYPE_FILTER_LABEL[typeFilter].color,
                background: TYPE_FILTER_LABEL[typeFilter].bg,
              }}
            >
              <i className="pi pi-filter" style={{ fontSize: 11 }} />
              {TYPE_FILTER_LABEL[typeFilter].label}
              <button
                onClick={() => onTypeFilterChange(null)}
                title="Show everyone"
                style={{
                  border: 'none',
                  background: 'rgba(255,255,255,.7)',
                  color: 'inherit',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <i className="pi pi-times" style={{ fontSize: 9 }} />
              </button>
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', marginRight: 4 }}>CLASS (optional)</span>
          <button
            className={`gl-class ${classId === null ? 'active' : ''}`}
            onClick={() => {
              setClassId(null);
              setSection(null);
              setPage(1);
            }}
          >
            Whole school
          </button>
          {classOptions.map((c) => (
            <button
              key={c.id}
              className={`gl-class ${classId === c.id ? 'active' : ''}`}
              onClick={() => {
                setClassId(classId === c.id ? null : c.id);
                setSection(null);
                setPage(1);
              }}
            >
              {c.label}
            </button>
          ))}
          {selectedClass?.sections && selectedClass.sections.length > 0 && (
            <>
              <span style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 4px' }} />
              {selectedClass.sections.map((sec) => (
                <button
                  key={sec}
                  className={`gl-class ${section === sec ? 'active' : ''}`}
                  onClick={() => {
                    setSection(section === sec ? null : sec);
                    setPage(1);
                  }}
                >
                  Sec {sec}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,.06)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        <DataTable
          value={rows}
          dataKey="_id"
          loading={loading}
          rowClassName={(r: LogRow) =>
            r.type === 'unauthorized' ? 'gl-row-alert' : r.type === 'late' ? 'gl-row-late' : ''
          }
          emptyMessage={
            <div style={{ padding: '56px 20px', textAlign: 'center' }}>
              <i className="pi pi-inbox" style={{ fontSize: 26, color: '#93c5fd' }} />
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1e40af', marginTop: 10 }}>
                {search || classId || direction !== 'all' || typeFilter ? 'Nothing matches these filters' : 'No gate records yet'}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {search || classId || direction !== 'all' || typeFilter
                  ? 'Clear the search, pick "All" or "Whole school", or remove the filter.'
                  : 'Entries and exits appear here as they are marked.'}
              </div>
            </div>
          }
        >
          <Column header="#" body={serialTemplate} style={{ width: 52 }} />
          <Column header="Time" body={timeTemplate} style={{ width: 100 }} />
          <Column header="Student" body={studentTemplate} />
          <Column header="Class" body={classTemplate} style={{ width: 110 }} />
          <Column header="" body={directionTemplate} style={{ width: 80 }} />
          <Column header="Type" body={typeTemplate} />
          <Column header="Gate / by" body={gateTemplate} style={{ width: 170 }} />
        </DataTable>
        <style>{`
          .gl-row-alert > td { background: rgba(254,242,242,.7) !important; }
          .gl-row-late > td { background: rgba(255,251,235,.8) !important; }
        `}</style>

        {pagination.total_pages > 1 && (
          <CustomPaginator
            page={pagination.current_page}
            totalPages={pagination.total_pages}
            totalRecords={pagination.total_items}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
            showInfoBar={false}
          />
        )}
      </div>
    </>
  );
};

const serialStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  background: '#f9fafb',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 12,
  color: '#6b7280',
  margin: '0 auto',
};


export default TodayGateLog;